import { useState, useEffect, useMemo, useCallback } from "react"
import { Trash2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
} from "@f2e/ui/components/base"
import { AlertBox } from "@/components"
import { PermissionGuard } from "@/components/Auth"
import { LoadingPage } from "@/components/System"
import {
  ClientChannel,
  ClientChannelListItem,
  ClientChannelType,
  Workspace,
  Permission,
} from "@/types"
import { useToast } from "@/hooks"
import {
  FetchStatus,
  useGetClientChannelApi,
  useDeleteClientChannelApi,
  useUnpublishClientChannelApi,
  clientChannelResponseToClientChannel,
} from "@/hooks/api"

import { ChannelConfigForm } from "./ChannelConfigForm"
import { ChannelOAuthBindings } from "./ChannelOAuthBindings"
import { ChannelOverview } from "./ChannelOverview"
import { ChannelTypeBadge } from "./ChannelTypeBadge"
import { ChannelPublishDialog } from "./ChannelPublishDialog"
import { PublishMenuButton } from "./PublishMenuButton"
import { deriveChannelPublishState } from "./publishState"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

const shouldShowOAuthTab = (channelType?: ClientChannelType): boolean =>
  channelType !== ClientChannelType.SLACK &&
  channelType !== ClientChannelType.LINE &&
  channelType !== ClientChannelType.WIDGET &&
  channelType !== ClientChannelType.MESSENGER &&
  channelType !== ClientChannelType.INSTAGRAM

export interface ChannelDetailProps {
  selectedChannelItem: ClientChannelListItem
  workspace: Workspace
  onChannelUpdated?: (channel: ClientChannel) => void
  onChannelDeleted?: (channelId: string) => void
}

export const ChannelDetail = ({
  selectedChannelItem,
  workspace,
  onChannelUpdated,
  onChannelDeleted,
}: ChannelDetailProps) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState("overview")
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false)
  const [channel, setChannel] = useState<ClientChannel | undefined>()
  const [hasUnsavedOverviewChanges, setHasUnsavedOverviewChanges] =
    useState(false)
  const [hasUnsavedConfigChanges, setHasUnsavedConfigChanges] = useState(false)
  const { successToast, alertToast } = useToast()

  // APIs
  const {
    data: channelRespData,
    error: channelRespError,
    status: channelStatus,
    fetchFn: getChannel,
  } = useGetClientChannelApi(
    workspace.organizationId || "",
    workspace.id,
    selectedChannelItem.id
  )

  const {
    data: deleteChannelRespData,
    error: deleteChannelRespError,
    status: deleteChannelRespStatus,
    fetchFn: deleteChannel,
  } = useDeleteClientChannelApi(
    workspace.organizationId || "",
    workspace.id,
    selectedChannelItem.id
  )
  const { fetchFn: unpublishChannel } = useUnpublishClientChannelApi(
    workspace.organizationId || "",
    workspace.id,
    selectedChannelItem.id
  )

  const handleChannelUpdated = useCallback(
    (updatedChannel: ClientChannel) => {
      setChannel(updatedChannel)
      onChannelUpdated?.(updatedChannel)
    },
    [onChannelUpdated]
  )

  const handleChannelPublished = useCallback(
    (updatedChannel: ClientChannel) => {
      handleChannelUpdated(updatedChannel)
    },
    [handleChannelUpdated]
  )

  // Load channel detail when selection changes
  useEffect(() => {
    if (!selectedChannelItem.id) {
      setChannel(undefined)
      return
    }
    // Clear previous channel so switching item shows loading state
    setChannel(undefined)
    setHasUnsavedOverviewChanges(false)
    setHasUnsavedConfigChanges(false)
    getChannel()
  }, [selectedChannelItem.id, getChannel])

  const hasUnsavedChanges = hasUnsavedOverviewChanges || hasUnsavedConfigChanges
  const publishState = useMemo(() => {
    if (!channel) return "live"
    return deriveChannelPublishState(channel, hasUnsavedChanges)
  }, [channel, hasUnsavedChanges])

  useEffect(() => {
    if (activeTab === "oauth" && !shouldShowOAuthTab(channel?.type)) {
      setActiveTab("overview")
    }
  }, [activeTab, channel?.type])

  // Handle channel detail data
  useEffect(() => {
    if (!channelRespData || !channelRespData.data) return
    const channelData = clientChannelResponseToClientChannel(
      channelRespData.data
    )
    // Ignore stale responses if the user has switched selection
    if (channelData.id !== selectedChannelItem.id) return
    setChannel(channelData)
  }, [channelRespData, selectedChannelItem.id])

  // Handle channel detail error
  useEffect(() => {
    if (!channelRespError) return
    alertToast({
      title: t("workspace.channels.messages.error.loadChannels.title"),
      description: getUserFacingApiErrorMessage(
        channelRespError,
        t,
        t("workspace.channels.messages.error.loadChannels.description")
      ),
    })
  }, [channelRespError, alertToast, t])

  const handleDelete = () => {
    deleteChannel()
      .then(() => {
        onChannelDeleted?.(selectedChannelItem.id)
      })
      .catch(() => undefined)
  }

  const handleOpenPublishDialog = () => {
    if (publishState === "unsaved_local") {
      alertToast({
        title: t("workspace.channels.messages.publishGuard.title"),
        description: t("workspace.channels.messages.publishGuard.description"),
      })
      return
    }
    setShowPublishDialog(true)
  }

  const handleUnpublish = () => {
    unpublishChannel({
      callbackFn: (resp) => {
        if (!resp || !resp.data) {
          alertToast({
            title: t(
              "workspace.channels.messages.error.unpublishChannel.title"
            ),
            description: t(
              "workspace.channels.messages.error.unpublishChannel.description"
            ),
          })
          return
        }

        const updatedChannel = clientChannelResponseToClientChannel(resp.data)
        handleChannelUpdated(updatedChannel)
        getChannel()
        successToast({
          title: t("workspace.channels.messages.unpublished.title"),
          description: t("workspace.channels.messages.unpublished.description"),
        })
        setShowUnpublishDialog(false)
      },
    }).catch((error) => {
      alertToast({
        title: t("workspace.channels.messages.error.unpublishChannel.title"),
        description: getUserFacingApiErrorMessage(
          error,
          t,
          t("workspace.channels.messages.error.unpublishChannel.description")
        ),
      })
    })
  }

  // Handle delete success
  useEffect(() => {
    if (!deleteChannelRespData) return
    successToast({
      title: t("workspace.channels.messages.deleted.title"),
      description: t("workspace.channels.messages.deleted.description"),
    })
    setShowDeleteDialog(false)
  }, [deleteChannelRespData, successToast, t])

  // Handle delete error
  useEffect(() => {
    if (!deleteChannelRespError) return
    alertToast({
      title: t("workspace.channels.messages.error.deleteChannel.title"),
      description: getUserFacingApiErrorMessage(
        deleteChannelRespError,
        t,
        t("workspace.channels.messages.error.deleteChannel.description")
      ),
    })
  }, [deleteChannelRespError, alertToast, t])

  const isLoading = useMemo(
    () => channelStatus === FetchStatus.Loading && !channel,
    [channelStatus, channel]
  )
  const showOAuthTab = shouldShowOAuthTab(channel?.type)

  if (isLoading || !channel) {
    return (
      <Card className="flex flex-col max-w-full overflow-hidden flex-1 min-h-0">
        <CardHeader className="py-3 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ChannelTypeBadge type={selectedChannelItem.type} />
              <CardTitle className="text-xl">
                {selectedChannelItem.name}
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 p-6 flex items-center justify-center">
          <LoadingPage />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="flex flex-col max-w-full overflow-hidden flex-1 min-h-0">
      <CardHeader className="py-3 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChannelTypeBadge type={channel.type} />
            <CardTitle className="text-xl">{channel.name}</CardTitle>
          </div>
          <PermissionGuard
            permission={Permission.WsUpdateResources}
            workspace={workspace}
          >
            <div className="flex items-start gap-2">
              <PublishMenuButton
                channel={channel}
                state={publishState}
                onPublish={handleOpenPublishDialog}
                onUnpublish={() => setShowUnpublishDialog(true)}
              />
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                disabled={deleteChannelRespStatus === FetchStatus.Loading}
              >
                <Trash2 className="h-4 w-4 text-white" />
              </Button>
            </div>
          </PermissionGuard>
        </div>
      </CardHeader>

      <CardContent className="flex-1 p-6 max-w-full overflow-hidden flex flex-col min-h-0">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full flex flex-col flex-1 min-h-0"
        >
          <TabsList
            className={`grid w-full shrink-0 ${showOAuthTab ? "grid-cols-3" : "grid-cols-2"}`}
          >
            <TabsTrigger value="overview">
              {t("workspace.channels.tabs.overview")}
            </TabsTrigger>
            <TabsTrigger value="config">
              {t("workspace.channels.tabs.config")}
            </TabsTrigger>
            {showOAuthTab && (
              <TabsTrigger value="oauth">
                {t("workspace.channels.tabs.oauth")}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent
            value="overview"
            className="py-6 flex-1 overflow-y-auto min-h-0"
          >
            <ChannelOverview
              channel={channel}
              workspace={workspace}
              onChannelUpdated={handleChannelUpdated}
              onUnsavedChangesChange={setHasUnsavedOverviewChanges}
            />
          </TabsContent>

          <TabsContent
            value="config"
            className="py-6 flex-1 overflow-y-auto min-h-0"
          >
            <ChannelConfigForm
              channel={channel}
              workspace={workspace}
              onChannelUpdated={handleChannelUpdated}
              onUnsavedChangesChange={setHasUnsavedConfigChanges}
            />
          </TabsContent>

          {showOAuthTab && (
            <TabsContent
              value="oauth"
              className="py-6 max-w-full flex-1 overflow-y-auto min-h-0"
            >
              <ChannelOAuthBindings channel={channel} workspace={workspace} />
            </TabsContent>
          )}
        </Tabs>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertBox
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={t("workspace.channels.delete.title")}
        description={t("workspace.channels.delete.description")}
        cancelText={t("workspace.channels.delete.cancel")}
        confirmText={
          deleteChannelRespStatus === FetchStatus.Loading
            ? t("common.loading")
            : t("workspace.channels.delete.confirm")
        }
        onConfirm={handleDelete}
        onCancel={(e) => e.stopPropagation()}
      />

      {/* Publish Confirmation Dialog */}
      <ChannelPublishDialog
        open={showPublishDialog}
        onOpenChange={setShowPublishDialog}
        channel={channel}
        workspace={workspace}
        onChannelPublished={handleChannelPublished}
      />

      {/* Unpublish Confirmation Dialog */}
      <AlertBox
        open={showUnpublishDialog}
        onOpenChange={setShowUnpublishDialog}
        title={t("workspace.channels.unpublish.title")}
        description={t("workspace.channels.unpublish.description")}
        cancelText={t("workspace.channels.unpublish.cancel")}
        confirmText={t("workspace.channels.unpublish.confirm")}
        onConfirm={handleUnpublish}
        onCancel={(e) => e.stopPropagation()}
      />
    </Card>
  )
}

export default ChannelDetail
